import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { PageTransition, FadeIn } from '../components/layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ChevronRight, Mail, Lock, User, Phone, Loader2, AlertCircle, Check } from 'lucide-react';

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  
  const { register } = useAuth();
  const navigate = useNavigate();

  const validateForm = () => {
    const errors = {};
    
    if (!formData.first_name.trim()) errors.first_name = 'Prénom requis';
    if (!formData.last_name.trim()) errors.last_name = 'Nom requis';
    if (!formData.email.trim()) errors.email = 'Email requis';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Email invalide';
    if (!formData.password) errors.password = 'Mot de passe requis';
    else if (formData.password.length < 6) errors.password = 'Minimum 6 caractères';
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear field error when user types
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setIsSubmitting(true);

    const result = await register({
      email: formData.email,
      password: formData.password,
      first_name: formData.first_name,
      last_name: formData.last_name,
      phone: formData.phone || null
    });
    
    if (result.success) {
      navigate('/mon-compte', { replace: true });
    } else {
      setError(result.error);
    }
    
    setIsSubmitting(false);
  };

  return (
    <PageTransition>
      {/* Hero */}
      <section className="pt-32 pb-8 px-6 bg-gradient-to-b from-[var(--champagne)]/30 to-white">
        <div className="max-w-[800px] mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light mb-4 text-[var(--charcoal)]"
          >
            Créer un compte
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-[var(--charcoal)]/60"
          >
            Rejoignez l'univers Institut Madame DSRS
          </motion.p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-[var(--champagne)]">
        <div className="max-w-[1400px] mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-[var(--charcoal)]/60">
            <Link to="/" className="hover:text-[var(--gold)] transition-colors">Accueil</Link>
            <ChevronRight size={14} />
            <span className="text-[var(--charcoal)]">Inscription</span>
          </nav>
        </div>
      </div>

      {/* Register Form */}
      <section className="py-16 px-6" data-testid="register-section">
        <div className="max-w-[500px] mx-auto">
          <FadeIn>
            <form
              onSubmit={handleSubmit}
              className="bg-white p-8 md:p-10 border border-[var(--champagne)] shadow-sm"
              data-testid="register-form"
            >
              {/* Error Message */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-3"
                  data-testid="register-error"
                >
                  <AlertCircle size={18} className="shrink-0 mt-0.5" />
                  <span>{error}</span>
                </motion.div>
              )}

              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <Label className="text-sm uppercase tracking-wider text-[var(--charcoal)]/70 mb-2 block">
                    Prénom <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--charcoal)]/40" />
                    <Input
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      placeholder="Marie"
                      className={`pl-10 rounded-none ${fieldErrors.first_name ? 'border-red-500' : 'border-[var(--champagne)]'}`}
                      data-testid="register-firstname"
                    />
                  </div>
                  {fieldErrors.first_name && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.first_name}</p>
                  )}
                </div>
                <div>
                  <Label className="text-sm uppercase tracking-wider text-[var(--charcoal)]/70 mb-2 block">
                    Nom <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    placeholder="Dupont"
                    className={`rounded-none ${fieldErrors.last_name ? 'border-red-500' : 'border-[var(--champagne)]'}`}
                    data-testid="register-lastname"
                  />
                  {fieldErrors.last_name && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.last_name}</p>
                  )}
                </div>
              </div>

              {/* Email Field */}
              <div className="mb-6">
                <Label className="text-sm uppercase tracking-wider text-[var(--charcoal)]/70 mb-2 block">
                  Email <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--charcoal)]/40" />
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="marie@exemple.com"
                    className={`pl-10 rounded-none ${fieldErrors.email ? 'border-red-500' : 'border-[var(--champagne)]'}`}
                    data-testid="register-email"
                  />
                </div>
                {fieldErrors.email && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>
                )}
              </div>

              {/* Phone Field */}
              <div className="mb-6">
                <Label className="text-sm uppercase tracking-wider text-[var(--charcoal)]/70 mb-2 block">
                  Téléphone <span className="text-[var(--charcoal)]/40">(optionnel)</span>
                </Label>
                <div className="relative">
                  <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--charcoal)]/40" />
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="06 00 00 00 00"
                    className="pl-10 rounded-none border-[var(--champagne)]"
                    data-testid="register-phone"
                  />
                </div>
              </div>

              {/* Password Fields */}
              <div className="mb-6">
                <Label className="text-sm uppercase tracking-wider text-[var(--charcoal)]/70 mb-2 block">
                  Mot de passe <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--charcoal)]/40" />
                  <Input
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    className={`pl-10 rounded-none ${fieldErrors.password ? 'border-red-500' : 'border-[var(--champagne)]'}`}
                    data-testid="register-password"
                  />
                </div>
                {fieldErrors.password && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.password}</p>
                )}
                <p className="text-xs text-[var(--charcoal)]/50 mt-1">Minimum 6 caractères</p>
              </div>

              <div className="mb-8">
                <Label className="text-sm uppercase tracking-wider text-[var(--charcoal)]/70 mb-2 block">
                  Confirmer le mot de passe <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--charcoal)]/40" />
                  <Input
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="••••••••"
                    className={`pl-10 rounded-none ${fieldErrors.confirmPassword ? 'border-red-500' : 'border-[var(--champagne)]'}`}
                    data-testid="register-confirm-password"
                  />
                </div>
                {fieldErrors.confirmPassword && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.confirmPassword}</p>
                )}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-6 bg-[var(--gold)] text-white text-xs uppercase tracking-[0.2em] font-medium hover:bg-[var(--dark-gold)] transition-colors duration-300 rounded-none disabled:opacity-50"
                data-testid="register-submit"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin mr-2" />
                    Création en cours...
                  </>
                ) : (
                  'Créer mon compte'
                )}
              </Button>

              {/* Login Link */}
              <p className="mt-6 text-center text-sm text-[var(--charcoal)]/60">
                Déjà un compte ?{' '}
                <Link
                  to="/login"
                  className="text-[var(--dark-rosegold)] hover:text-[var(--gold)] transition-colors font-medium"
                  data-testid="link-to-login"
                >
                  Se connecter
                </Link>
              </p>
            </form>
          </FadeIn>

          {/* Benefits */}
          <FadeIn delay={0.2}>
            <div className="mt-8 p-6 bg-[var(--champagne)]/20 border border-[var(--champagne)]">
              <h3 className="font-['Cormorant_Garamond'] text-xl mb-4 text-[var(--charcoal)]">
                Avantages de votre compte
              </h3>
              <ul className="space-y-2">
                {[
                  'Suivez vos commandes Press On Nails',
                  'Historique de vos prestations',
                  'Offres exclusives réservées aux membres',
                  'Prise de rendez-vous simplifiée'
                ].map((benefit, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[var(--charcoal)]/70">
                    <Check size={14} className="text-[var(--gold)]" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          </FadeIn>
        </div>
      </section>
    </PageTransition>
  );
}
